import { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LogOut, RefreshCw, Calendar, CheckCircle2, Clock, XCircle, Users, MessageCircle, Sparkles, TrendingUp } from "lucide-react";
import { clientWhatsappLink } from "@/lib/config";
import { Link } from "react-router-dom";
import { HandoffPanel } from "@/components/HandoffPanel";
import { ConversationsPanel } from "@/components/ConversationsPanel";
import { AdminSecurity } from "@/components/AdminSecurity";


const STATUS = [
  { value: "pendente", label: "Pendente" },
  { value: "confirmado", label: "Confirmado" },
  { value: "concluido", label: "Concluído" },
  { value: "cancelado", label: "Cancelado" },
  { value: "recusado", label: "Recusado" },
  { value: "invalido", label: "Inválido/Teste" },
];

const statusColor = {
  pendente: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  confirmado: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  concluido: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  cancelado: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  recusado: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  invalido: "bg-neutral-500/15 text-neutral-400 border-neutral-500/30",
};

const buildConfirmMsg = (a) => {
  const parts = [
    `Olá, ${a.name}! 😊`,
    "",
    "Seu horário na EZ Capelli Studio foi confirmado.",
    `Serviço: ${a.service}`,
  ];
  if (a.preferred_date) parts.push(`Data: ${a.preferred_date}`);
  if (a.preferred_time) parts.push(`Horário: ${a.preferred_time}`);
  if (a.value) parts.push(`Valor: ${a.value}`);
  parts.push("", "Esperamos você!");
  return parts.join("\n");
};

const buildRescheduleMsg = (a) =>
  `Olá, ${a.name}! Aqui é da EZ Capelli Studio. Recebi sua solicitação de ${a.service}${
    a.preferred_date ? ` para ${a.preferred_date}` : ""
  }${a.preferred_time ? ` às ${a.preferred_time}` : ""}. Infelizmente esse horário não está disponível. Podemos sugerir outro? 💛`;

export default function Admin() {
  const navigate = useNavigate();
  const logout = async () => {
    await adminApi.post("/auth/logout");
    navigate("/admin/login", { replace: true });
  };
  const [appts, setAppts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pendente: 0,
    confirmado: 0,
    concluido: 0,
    chats: { total: 0, today: 0, week: 0, month: 0, messages_total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [agendaDate, setAgendaDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [agendaAppts, setAgendaAppts] = useState([]);
  const [settings, setSettings] = useState({ reschedule_min_hours: 48, default_signal_amount: 0 });

  const loadAgenda = async (date) => {
    try {
      const res = await adminApi.get(`/agenda/day`, { params: { date } });
      setAgendaAppts(res.data);
    } catch {
      /* ignore */
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [a, s, ag, st] = await Promise.all([
        adminApi.get(`/appointments`),
        adminApi.get(`/stats`),
        adminApi.get(`/agenda/day`, { params: { date: agendaDate } }),
        adminApi.get(`/settings`),
      ]);
      setAppts(a.data);
      setStats(s.data);
      setAgendaAppts(ag.data);
      setSettings(st.data);
    } catch (e) {
      toast.error("Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status, force = false) => {
    try {
      await adminApi.patch(`/appointments/${id}/status`, { status, force });
      toast.success("Status atualizado");
      load();
      return true;
    } catch (e) {
      if (e.response?.status === 409 && window.confirm(`${e.response.data.detail} Confirmar mesmo assim?`)) {
        return updateStatus(id, status, true);
      }
      if (e.response?.status !== 409) toast.error("Erro ao atualizar");
      return false;
    }
  };

  // Mark as confirmed first; only then open WhatsApp with the confirmation message.
  const confirmAppt = async (a) => {
    const win = window.open("", "_blank");
    const ok = await updateStatus(a.id, "confirmado");
    if (ok && win) win.location.href = clientWhatsappLink(a.phone, buildConfirmMsg(a));
    else if (win) win.close();
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  };

  const statCards = [
    { label: "Total", value: stats.total, icon: Users, color: "text-[#D4AF37]" },
    { label: "Pendentes", value: stats.pendente, icon: Clock, color: "text-amber-400" },
    { label: "Confirmados", value: stats.confirmado, icon: CheckCircle2, color: "text-emerald-400" },
    { label: "Concluídos", value: stats.concluido, icon: Calendar, color: "text-sky-400" },
  ];

  const summaryCards = [
    { id: "today", label: "Atendimentos de hoje", value: stats.today_appointments, icon: Calendar, color: "text-[#D4AF37]" },
    { id: "new-clients", label: "Clientes novos (7 dias)", value: stats.new_clients_7d, icon: Users, color: "text-emerald-400" },
    { id: "handoffs", label: "Pedidos de atendimento humano", value: stats.handoffs_open, icon: MessageCircle, color: "text-amber-400" },
  ];

  const chatCards = [
    { label: "Hoje", value: stats.chats?.today ?? 0, icon: Sparkles, color: "text-[#D4AF37]", hint: "conversas" },
    { label: "7 dias", value: stats.chats?.week ?? 0, icon: TrendingUp, color: "text-emerald-400", hint: "conversas" },
    { label: "30 dias", value: stats.chats?.month ?? 0, icon: MessageCircle, color: "text-sky-400", hint: "conversas" },
    { label: "Total", value: stats.chats?.total ?? 0, icon: Users, color: "text-violet-400", hint: `${stats.chats?.messages_total ?? 0} mensagens` },
  ];

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-[#222] bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/" data-testid="admin-logo-link" className="font-heading text-2xl font-light">
              EZ <span className="gold-gradient-text italic">Capelli</span>{" "}
              <span className="overline ml-2">Painel</span>
            </Link>
            <p className="text-xs text-white/50 mt-1">
              Gestão de agendamentos
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="btn-secondary-outline text-xs"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Atualizar
            </button>
            <AdminSecurity />
            <button data-testid="admin-logout-btn" onClick={logout} className="btn-secondary-outline text-xs">
              <LogOut size={14} />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="overline">Resumo</span>
        </div>
        <section data-testid="admin-summary" className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
          {summaryCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.id} data-testid={`summary-${s.id}`} className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="overline">{s.label}</span>
                  <Icon size={20} strokeWidth={1.5} className={s.color} />
                </div>
                <div className="font-heading text-5xl font-light mt-4">{s.value ?? 0}</div>
              </div>
            );
          })}
        </section>

        <HandoffPanel />

        {/* Appointments stats */}
        <div className="mb-3 flex items-center gap-2">
          <Calendar size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="overline">Agendamentos</span>
        </div>
        <section data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`stat-${s.label.toLowerCase()}`}
                className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="overline">{s.label}</span>
                  <Icon size={20} strokeWidth={1.5} className={s.color} />
                </div>
                <div className="font-heading text-5xl font-light mt-4">{s.value}</div>
              </div>
            );
          })}
        </section>

        {/* Chat IA stats */}
        <div className="mb-3 flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="overline">Conversas com a IA</span>
        </div>
        <section data-testid="admin-chat-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {chatCards.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.label}
                data-testid={`chat-stat-${s.label.toLowerCase().replace(" ", "-")}`}
                className="bg-[#0A0A0A] border border-[#222] p-6 hover:border-[#D4AF37]/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="overline">{s.label}</span>
                  <Icon size={20} strokeWidth={1.5} className={s.color} />
                </div>
                <div className="font-heading text-5xl font-light mt-4">{s.value}</div>
                <div className="text-xs text-white/40 mt-1">{s.hint}</div>
              </div>
            );
          })}
        </section>

        <ConversationsPanel />

        {/* Agenda do Dia */}
        <div className="mb-3 flex items-center gap-2 mt-2">
          <Calendar size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="overline">Agenda do dia</span>
        </div>
        <section data-testid="admin-agenda-day" className="bg-[#0A0A0A] border border-[#222] mb-12">
          <div className="p-6 border-b border-[#222] flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-light">Agenda</h2>
              <input
                data-testid="agenda-date-picker"
                type="date"
                value={agendaDate}
                onChange={(e) => {
                  setAgendaDate(e.target.value);
                  loadAgenda(e.target.value);
                }}
                className="bg-[#141414] border border-[#222] text-white text-sm px-3 py-1.5 focus:border-[#D4AF37] outline-none"
              />
            </div>
            <span className="text-xs text-white/50">
              {agendaAppts.length} agendamento{agendaAppts.length !== 1 ? "s" : ""}
            </span>
          </div>
          {agendaAppts.length === 0 ? (
            <div className="p-10 text-center text-white/50 text-sm">
              Nenhum agendamento para essa data.
            </div>
          ) : (
            <div className="divide-y divide-[#222]">
              {agendaAppts.map((a) => (
                <div
                  key={a.id}
                  data-testid={`agenda-row-${a.id}`}
                  className="p-4 flex items-center gap-4 hover:bg-[#111]"
                >
                  <div className="text-center flex-shrink-0 w-16">
                    <div className="font-heading text-2xl text-[#D4AF37] font-light">
                      {a.preferred_time || "—"}
                    </div>
                    {a.duration_minutes && (
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">
                        {a.duration_minutes} min
                      </div>
                    )}
                  </div>
                  <div className="w-px h-12 bg-[#222] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-medium">{a.name}</span>
                      <Badge className={`${statusColor[a.status] || statusColor.pendente} border text-[10px]`}>
                        {a.status}
                      </Badge>
                    </div>
                    <div className="text-[#D4AF37] text-sm mt-0.5">{a.service}</div>
                    {a.value && (
                      <div className="text-xs text-white/50">{a.value}</div>
                    )}
                  </div>
                  <a
                    href={clientWhatsappLink(a.phone, `Olá ${a.name}!`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#25D366] hover:underline whitespace-nowrap"
                  >
                    WhatsApp →
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Settings */}
        <div className="mb-3 flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
          <span className="overline">Configurações</span>
        </div>
        <section data-testid="admin-settings" className="bg-[#0A0A0A] border border-[#222] p-6 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">
                Antecedência mínima para reagendamento (horas)
              </label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="settings-reschedule-hours"
                  type="number"
                  min="0"
                  value={settings.reschedule_min_hours}
                  onChange={(e) =>
                    setSettings({ ...settings, reschedule_min_hours: parseInt(e.target.value || "0") })
                  }
                  className="bg-[#141414] border border-[#222] text-white text-sm px-3 py-2 w-24 focus:border-[#D4AF37] outline-none"
                />
                <span className="text-xs text-white/50">horas (padrão: 48)</span>
              </div>
            </div>
            <div>
              <label className="overline block mb-2">
                Valor padrão do sinal (R$)
              </label>
              <div className="flex items-center gap-2">
                <input
                  data-testid="settings-signal-amount"
                  type="number"
                  min="0"
                  step="10"
                  value={settings.default_signal_amount}
                  onChange={(e) =>
                    setSettings({ ...settings, default_signal_amount: parseFloat(e.target.value || "0") })
                  }
                  className="bg-[#141414] border border-[#222] text-white text-sm px-3 py-2 w-24 focus:border-[#D4AF37] outline-none"
                />
                <span className="text-xs text-white/50">0 desativa cobrança de sinal</span>
              </div>
            </div>
          </div>
          <button
            data-testid="settings-save-btn"
            onClick={async () => {
              try {
                await adminApi.patch(`/settings`, {
                  reschedule_min_hours: settings.reschedule_min_hours,
                  default_signal_amount: settings.default_signal_amount,
                });
                toast.success("Configurações salvas");
              } catch {
                toast.error("Erro ao salvar");
              }
            }}
            className="btn-primary-gold text-xs mt-6"
          >
            Salvar configurações
          </button>
        </section>

        {/* Appointments table */}
        <section data-testid="admin-appointments" className="bg-[#0A0A0A] border border-[#222]">
          <div className="p-6 border-b border-[#222] flex items-center justify-between">
            <h2 className="font-heading text-2xl font-light">Agendamentos</h2>
            <span className="text-xs text-white/50">{appts.length} registros</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-white/50">Carregando...</div>
          ) : appts.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              Nenhum agendamento ainda. Compartilhe o link do site! ✨
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#222] hover:bg-transparent">
                    <TableHead className="text-white/60">Cliente</TableHead>
                    <TableHead className="text-white/60">WhatsApp</TableHead>
                    <TableHead className="text-white/60">Serviço</TableHead>
                    <TableHead className="text-white/60">Data preferida</TableHead>
                    <TableHead className="text-white/60">Criado em</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-white/60">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appts.map((a) => (
                    <TableRow
                      key={a.id}
                      data-testid={`appointment-row-${a.id}`}
                      className="border-[#222] hover:bg-[#111]"
                    >
                      <TableCell>
                        <div className="font-medium">{a.name}</div>
                        {a.source === "chat_ia" && (
                          <div className="text-[10px] text-[#D4AF37] mt-0.5 tracking-wider uppercase">
                            via IA
                          </div>
                        )}
                        {a.notes && (
                          <div className="text-xs text-white/40 mt-1 max-w-[200px] truncate">
                            {a.notes}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-white/80">{a.phone}</TableCell>
                      <TableCell className="text-[#D4AF37]">
                        <div>{a.service}</div>
                        {a.value && (
                          <div className="text-xs text-white/50">{a.value}</div>
                        )}
                        {a.duration_minutes && (
                          <div className="text-[10px] text-white/40">{a.duration_minutes} min</div>
                        )}
                      </TableCell>
                      <TableCell className="text-white/70">
                        <div>{a.preferred_date || "—"}</div>
                        {a.preferred_time && (
                          <div className="text-xs text-white/50">{a.preferred_time}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-white/50 text-xs">
                        {fmt(a.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusColor[a.status] || statusColor.pendente} border whitespace-nowrap`}>
                          {a.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1.5">
                          {a.status === "pendente" && (
                            <div className="flex gap-1.5">
                              <button
                                data-testid={`confirm-btn-${a.id}`}
                                onClick={() => confirmAppt(a)}
                                className="text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/25 px-2 py-1 whitespace-nowrap"
                                title="Marca como confirmado e, em seguida, abre o WhatsApp da cliente com a confirmação"
                              >
                                Confirmar
                              </button>
                              <a
                                data-testid={`reschedule-btn-${a.id}`}
                                href={clientWhatsappLink(a.phone, buildRescheduleMsg(a))}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-400 border border-amber-500/40 hover:bg-amber-500/25 px-2 py-1 whitespace-nowrap"
                                title="Sugerir outro horário via WhatsApp"
                              >
                                Sugerir outro
                              </a>
                              <button
                                data-testid={`refuse-btn-${a.id}`}
                                onClick={() => updateStatus(a.id, "recusado")}
                                className="text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-400 border border-rose-500/40 hover:bg-rose-500/25 px-2 py-1 whitespace-nowrap"
                              >
                                Recusar
                              </button>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Select
                              value={a.status}
                              onValueChange={(v) => updateStatus(a.id, v)}
                            >
                              <SelectTrigger
                                data-testid={`status-select-${a.id}`}
                                className="w-[150px] bg-[#141414] border-[#333] text-white text-xs h-7 rounded-none"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A0A0A] border-[#333] text-white rounded-none">
                                {STATUS.map((st) => (
                                  <SelectItem key={st.value} value={st.value}>
                                    {st.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <a
                              data-testid={`wa-link-${a.id}`}
                              href={clientWhatsappLink(
                                a.phone,
                                `Olá ${a.name}! Aqui é da EZ Capelli Studio.`
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-[#D4AF37] hover:underline whitespace-nowrap"
                            >
                              WhatsApp →
                            </a>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
